<script setup lang="ts">
import type { AuthorDTO } from '~/shared/api/client/authors/authors.dto';
import type { PersonDTO } from '~/shared/api/client/persons/persons.dto';
import type { TagDTO } from '~/shared/api/client/tags/tags.dto';
import type { MaterialAITagDTO } from '~/shared/api/client/tags/ai-tags.dto';
import type { IArticleCategory } from '../model/article.interfaces';

import ArticleCategories from './ArticleCategories.vue';
import {
  CLIENT_NEWS_BY_AUTHOR_URL,
  CLIENT_TAG_URL,
} from '~/shared/api/client/news/news.const';
import { getAuthorName } from '~/shared/lib/getAuthorName';
import { resizeImageAttrs } from '~/shared/lib/resizeImage';

import authorAvatar from './images/authorAvatar.svg';

const props = defineProps<{
  item: AuthorDTO | null;
  person?: PersonDTO | null;
  categoryName: string | false;
  categoryLink: string | false;
  /** Новостные теги (sectors). Выглядят как корневой тег. */
  sectors?: TagDTO[];
  /** AI-теги материала. Выводятся со значком. */
  aiTags?: MaterialAITagDTO[];
}>();

const link = `${CLIENT_NEWS_BY_AUTHOR_URL}${props?.item?.id}`;
const name = getAuthorName(props.item);

const hasPeople = computed(() => Boolean(props.person || props.item));

// Сначала корневой тег (категория), затем новостные теги (sectors).
// Оба выглядят одинаково и выводятся одним списком.
const categories = computed<IArticleCategory[]>(() => {
  const result: IArticleCategory[] = [];

  if (props.categoryName && props.categoryLink) {
    result.push({ title: props.categoryName, link: props.categoryLink });
  }

  for (const sector of props.sectors ?? []) {
    result.push({
      title: sector.name,
      link: `${CLIENT_TAG_URL}${sector.id}`,
    });
  }

  return result;
});
</script>

<template>
  <div
    :class="[
      $style.summary,
      {
        [$style.summary_noItem!]: !hasPeople,
      },
    ]"
  >
    <template v-if="props.person">
      <div :class="[$style.summary__element, $style.summary__photoWrapper]">
        <img
          :src="
            resizeImageAttrs(props.person.photo?.file_url || authorAvatar, {
              width: 60,
              height: 60,
              mode: 'fill',
            }).src
          "
          :srcset="
            resizeImageAttrs(props.person.photo?.file_url || authorAvatar, {
              width: 60,
              height: 60,
              mode: 'fill',
            }).srcset
          "
          :title="props.person.fio"
          :alt="props.person.fio"
          :class="$style.summary__photo"
        />
      </div>
      <div
        :class="[
          $style.summary__element,
          $style.summary__author,
          { [$style.summary__author_wide!]: !props.person.company?.name },
        ]"
      >
        <div :class="$style.summary__author__header">Эксперт</div>
        <div :class="$style.summary__author__inner">
          <div :class="$style.summary__author__name">
            {{ props.person.fio }}
          </div>
          <div :class="$style.summary__author__description">
            {{ props.person.position }}
          </div>
          <div
            v-if="props.person.company?.name"
            :class="[
              $style.summary__author__description,
              $style.summary__author__company,
            ]"
          >
            {{ props.person.company.name }}
          </div>
        </div>
      </div>
      <div
        v-if="props.person.company?.name"
        :class="[
          $style.summary__element,
          $style.summary__articles,
          $style.summary__company,
        ]"
      >
        <div :class="$style.summary__articles__header">Компания</div>
        <div :class="$style.summary__articles__text">
          {{ props.person.company.name }}
        </div>
      </div>
    </template>
    <template v-if="props.item">
      <div :class="[$style.summary__element, $style.summary__photoWrapper]">
        <img
          :src="
            resizeImageAttrs(props.item?.photo?.file_url || authorAvatar, {
              width: 60,
              height: 60,
              mode: 'fill',
            }).src
          "
          :srcset="
            resizeImageAttrs(props.item?.photo?.file_url || authorAvatar, {
              width: 60,
              height: 60,
              mode: 'fill',
            }).srcset
          "
          :title="name"
          :alt="name"
          :class="$style.summary__photo"
        />
      </div>
      <div :class="[$style.summary__element, $style.summary__author]">
        <div :class="$style.summary__author__header">Автор</div>
        <div :class="$style.summary__author__inner">
          <div :class="$style.summary__author__name">
            {{ name }}
          </div>
          <div :class="$style.summary__author__description">
            {{ props.item.position }}
          </div>
        </div>
      </div>
      <div :class="[$style.summary__element, $style.summary__articles]">
        <div :class="$style.summary__articles__header">Читать</div>
        <NuxtLink :class="$style.summary__articles__link" :to="link">
          Другие статьи автора
        </NuxtLink>
      </div>
    </template>
    <div
      :class="[
        $style.summary__element,
        $style.summary__back,
        { [$style.summary__back_wide!]: !props.item },
      ]"
    >
      <ButtonBack />
    </div>
    <div
      :class="[
        $style.summary__element,
        $style.summary__categories,
        { [$style.summary__categories_empty!]: !categories.length },
      ]"
    >
      <!-- вместе с временно скрытыми AI-тегами убрано из условия: || aiTags?.length -->
      <ArticleCategories
        v-if="categories.length"
        :categories="categories"
        :ai-tags="aiTags"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.summary {
  display: grid;
  // Auto-placed rows: one per person, then back + categories.
  grid-template-columns: 100px 1fr auto;
  grid-auto-rows: auto;
  border-top: 1px solid var(--color-gray-line);
  border-left: 1px solid var(--color-gray-line);

  &_noItem {
    grid-template-columns: 100px 1fr;
  }

  &__element {
    border-right: 1px solid var(--color-gray-line);
    border-bottom: 1px solid var(--color-gray-line);
    padding: var(--space-2-5x);
  }

  &__photo {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    object-position: top;

    &Wrapper {
      width: 100px;
    }
  }

  &__author {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-0-5x);

    &__header {
      @include text_caption1_caps;
      color: var(--color-gray-text);
    }

    &__inner {
      display: flex;
      flex-flow: column nowrap;
    }

    &__name {
      @include text_body1_medium;
      color: var(--color-black-1);
    }

    &__description {
      @include text_body2;
      color: var(--color-gray-text);
    }

    &__company {
      display: none;
    }
  }

  &__articles {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-0-5x);
    padding-top: 30px;
    padding-bottom: 30px;

    &__header {
      @include text_caption1_caps;
      color: var(--color-gray-text);
    }

    &__link {
      @include text_body1_medium;
      color: var(--color-red-1);
      cursor: pointer;
      text-decoration: none;
    }

    &__text {
      @include text_body1_medium;
      color: var(--color-black-1);
    }
  }

  &__back {
    padding: var(--space-2-5x) 0;
    justify-content: center;
    align-content: center;
  }

  &__categories {
    grid-column: span 2;
    display: flex;
    align-items: center;
  }
}

@media (min-width: 600px) {
  /* Без автора колонок две — категории встают рядом с «Назад», а не на новую строку */
  .summary_noItem .summary__categories {
    grid-column: auto;
  }

  /* Компании нет — эксперт занимает и её колонку */
  .summary__author_wide {
    grid-column: span 2;
  }
}

@media (max-width: 599px) {
  .summary {
    grid-template-columns: 100px auto;

    &__company {
      display: none;
    }

    &__author__company {
      display: block;
    }

    &__articles {
      order: 2;
    }

    &__back {
      order: 1;
    }

    &__categories {
      order: 3;

      &_empty {
        display: none;
      }
    }

    &__back_wide {
      grid-column: span 2;
    }
  }
}
</style>
